import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import AiDescribeButton from "./AiDescribeButton";

export const Field = ({ label, id, children }) => (
  <div className="space-y-2"><Label htmlFor={id}>{label}</Label>{children}</div>
);

/** Generic listing form: `fields` = [{key,label,type}] , `endpoint` = "stocks" | "properties". */
export default function ListingFormDialog({ open, onOpenChange, item, empty, fields, endpoint, assetType, title, onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(item ? { ...empty, ...item } : empty); }, [open, item, empty]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const facts = Object.fromEntries(fields.map((f) => [f.label, form[f.key]]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form };
    fields.forEach((f) => { if (f.type === "number") payload[f.key] = Number(payload[f.key]); });
    try {
      if (item) await api.put(`/admin/${endpoint}/${item.id}`, payload);
      else await api.post(`/admin/${endpoint}`, payload);
      toast.success(`${title} ${item ? "updated" : "created"}`);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-line bg-panel sm:max-w-2xl" data-testid={`${assetType}-form-dialog`}>
        <DialogHeader><DialogTitle className="font-display text-2xl">{item ? "Edit" : "Add"} {title.toLowerCase()}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            {fields.map((f) => (
              <Field key={f.key} label={f.label} id={`${assetType}-${f.key}`}>
                <Input id={`${assetType}-${f.key}`} type={f.type || "text"} step={f.step || "any"} required={f.required !== false}
                  value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} className="field" data-testid={`${assetType}-form-${f.key}-input`} />
              </Field>
            ))}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor={`${assetType}-description`}>Description</Label>
              <AiDescribeButton assetType={assetType} details={facts} onText={(t) => set("description", t)} />
            </div>
            <Textarea id={`${assetType}-description`} rows={7} value={form.description} onChange={(e) => set("description", e.target.value)}
              className="rounded-xl border-line bg-surface" data-testid={`${assetType}-form-description-input`} />
          </div>
          <label className="flex items-center gap-3 text-sm text-slate-300">
            <Switch checked={!!form.featured} onCheckedChange={(v) => set("featured", v)} data-testid={`${assetType}-form-featured-switch`} />
            Feature on the home page
          </label>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid={`${assetType}-form-submit-button`}>{busy ? "Saving..." : "Save listing"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
